import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, FileText, Loader2, FolderOpen, ChevronLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useGoogleDriveFiles, useDropboxFiles, CloudFile } from "@/hooks/useCloudConnections";
import { useToast } from "@/hooks/use-toast";

interface CloudFilePickerProps {
  provider: "google" | "dropbox";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (content: string) => void;
}

export function CloudFilePicker({
  provider,
  open,
  onOpenChange,
  onImport,
}: CloudFilePickerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [importing, setImporting] = useState<string | null>(null);

  const googleDrive = useGoogleDriveFiles();
  const dropbox = useDropboxFiles();

  const isGoogle = provider === "google";
  const { files, loading, error, listFiles, importFile, currentPath, navigateUp } = isGoogle
    ? { ...googleDrive, currentPath: "", navigateUp: () => {} }
    : dropbox;

  useEffect(() => {
    if (open) {
      listFiles();
    }
  }, [open, listFiles]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      listFiles(isGoogle ? undefined : currentPath, searchQuery);
    } else {
      listFiles(isGoogle ? undefined : currentPath);
    }
  };

  const handleFileClick = async (file: CloudFile) => {
    if (file.isFolder) {
      // Navigate into folder (Dropbox only)
      if (!isGoogle) {
        dropbox.listFiles(file.path);
      }
      return;
    }

    setImporting(file.id);
    try {
      const content = await importFile(isGoogle ? file.id : file.path!);
      if (content) {
        onImport(content);
        onOpenChange(false);
        toast({
          title: t("cloudImport.importSuccess"),
          description: file.name,
        });
      }
    } catch (err) {
      console.error("Import error:", err);
      toast({
        title: t("cloudImport.importError"),
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setImporting(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isGoogle ? t("cloudImport.googleDocs.title") : t("cloudImport.dropbox.title")}
          </DialogTitle>
          <DialogDescription>
            {isGoogle ? t("cloudImport.googleDocs.description") : t("cloudImport.dropbox.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          {!isGoogle && currentPath && (
            <Button variant="outline" size="icon" onClick={navigateUp}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("cloudImport.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-10"
            />
          </div>
          <Button variant="outline" size="icon" onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => listFiles(isGoogle ? undefined : currentPath)}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {!isGoogle && currentPath && (
          <div className="text-sm text-muted-foreground mb-2 truncate">
            {currentPath}
          </div>
        )}

        <div className="flex-1 overflow-y-auto border rounded-md">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <p className="text-destructive">{error}</p>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <FileText className="h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">{t("cloudImport.noFiles")}</p>
            </div>
          ) : (
            <div className="divide-y">
              {files.map((file) => (
                <button
                  key={file.id}
                  onClick={() => handleFileClick(file)}
                  disabled={importing !== null}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors",
                    importing === file.id && "bg-muted/50"
                  )}
                >
                  {file.isFolder ? (
                    <FolderOpen className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.name}</p>
                    {file.modifiedTime && (
                      <p className="text-xs text-muted-foreground">
                        {formatDate(file.modifiedTime)}
                      </p>
                    )}
                  </div>
                  {importing === file.id && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
